import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import {
  KeyValue, Pill, Spinner, FormGrid, Field, TextInput, NumberInput, DateInput,
  TextArea, SelectInput, LineTable,
} from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import {
  num, dateLabel, returnStatus, returnType, returnDisposition, inspectionResult,
} from '../../lib/format';
import type {
  Return, ReturnLine, ReturnType, ReturnDisposition,
  Customer, Supplier, Contract, Delivery, Item, Warehouse,
} from '../../lib/types';

const today = () => new Date().toISOString().slice(0, 10);

// 常见退货原因（仅作为快捷填入，reason_code 后端为自由文本必填）。
const REASON_PRESETS = ['质量问题', '规格不符', '运输破损', '多发/错发', '客户取消', '来料不良'];

// 解析序列号输入：逗号 / 空格 / 换行分隔 → 字符串数组（去空）。
const parseSerials = (raw: string): string[] =>
  raw.split(/[\s,，、]+/).map((s) => s.trim()).filter(Boolean);

// —— 处理面板：客户退货→回库(入库方向) / 供应商退料→退还供应商(出库方向)，单事务过账库存流水 ——
const CUSTOMER_DISPOSITIONS: { value: ReturnDisposition; label: string }[] = [
  { value: 'restock', label: '回库（良品入库）' },
  { value: 'repair', label: '返修' },
  { value: 'replace', label: '换货' },
  { value: 'scrap', label: '报废' },
];

function ResolvePanel({ ret, onDone, onCancel }: { ret: Return; onDone: () => void; onCancel: () => void }) {
  const isSupplierReturn = ret.return_type === 'supplier_material_return';
  const [disposition, setDisposition] = useState<ReturnDisposition>(isSupplierReturn ? 'return_to_supplier' : 'restock');
  const [warehouseId, setWarehouseId] = useState<string>(ret.warehouse_id ?? '');
  const [warehouseLabel, setWarehouseLabel] = useState('');
  const [resolutionDate, setResolutionDate] = useState(today());
  const [notes, setNotes] = useState(ret.reason_detail ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!resolutionDate.trim()) { setErr('处理日期为必填'); return; }
    if (!warehouseId) { setErr('请选择处理仓库'); return; }
    setBusy(true);
    try {
      await api.post(`/api/returns/${ret.id}/resolve`, {
        disposition,
        warehouse_id: warehouseId,
        resolution_date: resolutionDate,
        notes: notes || null,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '处理失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="notice info" style={{ display: 'block', marginTop: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>处理退货单</div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        {isSupplierReturn
          ? '供应商退料：处理方式固定为「退还供应商」，将按退货明细从所选仓库生成出库库存流水（库存 −）。'
          : '客户退货：按所选处理方式生成入库库存流水，货物流入所选仓库（库存 +）。换货/返修/报废的后续闭环待业务确认后细化，本步骤先固化库存方向与原单关联。'}
      </div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      <FormGrid min={180}>
        <Field label="处理方式" required>
          {isSupplierReturn ? (
            <SelectInput value="return_to_supplier" disabled onChange={() => {}}>
              <option value="return_to_supplier">退还供应商</option>
            </SelectInput>
          ) : (
            <SelectInput value={disposition} onChange={(e) => setDisposition(e.target.value as ReturnDisposition)}>
              {CUSTOMER_DISPOSITIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </SelectInput>
          )}
        </Field>
        <Field label="处理仓库" required>
          <SearchSelect<Warehouse> endpoint="/api/warehouses" value={warehouseId || null} label={warehouseLabel}
            onChange={(row) => { setWarehouseId(row?.id ?? ''); setWarehouseLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
            optionTitle={(w) => `${w.code} · ${w.name_zh}`} placeholder="选择仓库…" />
        </Field>
        <Field label="处理日期" required><DateInput value={resolutionDate} onChange={(e) => setResolutionDate(e.target.value)} /></Field>
        <Field label="处理备注" span><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="默认沿用退货原因说明" /></Field>
      </FormGrid>
      <div className="detail-actions">
        <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '过账中…' : '确认处理（过账库存流水）'}</button>
        <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
      </div>
    </div>
  );
}

// —— 退货单详情：关键字段 + 明细 + 处理（生成库存流水）——
function ReturnDetail({ row, onChanged }: { row: Return; onChanged: () => void }) {
  const { user } = useAuth();
  const canWrite = can(user, 'returns', 'create');
  const { data, loading, error, reload } = useDetail<Return>(`/api/returns/${row.id}`);
  const [resolving, setResolving] = useState(false);
  const ret = data ?? row;
  const lines = ret.lines ?? [];
  const finished = ret.status === 'resolved' || ret.status === 'cancelled';

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{ret.number}</span>
        <h3>{ret.customer_name || ret.supplier_name || (ret.return_type === 'customer_return' ? '客户退货' : '供应商退料')}</h3>
        <Pill tone={returnType(ret.return_type).tone} label={returnType(ret.return_type).label} />
        <Pill tone={returnStatus(ret.status).tone} label={returnStatus(ret.status).label} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      <div className="kv-grid">
        {ret.return_type === 'customer_return'
          ? <KeyValue k="客户">{ret.customer_name || ret.customer_id || '—'}</KeyValue>
          : <KeyValue k="供应商">{ret.supplier_name || ret.supplier_id || '—'}</KeyValue>}
        <KeyValue k="退货日期">{dateLabel(ret.return_date)}</KeyValue>
        <KeyValue k="退货原因">{ret.reason_code || '—'}</KeyValue>
        <KeyValue k="原因说明">{ret.reason_detail || '—'}</KeyValue>
        <KeyValue k="原销售合同">{ret.contract_number || '—'}</KeyValue>
        <KeyValue k="原交货单">{ret.delivery_number || '—'}</KeyValue>
        <KeyValue k="处理方式">{ret.disposition ? returnDisposition(ret.disposition).label : '—'}</KeyValue>
        <KeyValue k="处理仓库">{ret.warehouse || ret.warehouse_id || '—'}</KeyValue>
        <KeyValue k="库存流水">{ret.stock_movement_id || '—'}</KeyValue>
        <KeyValue k="红冲/信用凭证">{ret.credit_note_reference || '—'}</KeyValue>
      </div>

      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '12px 0 4px' }}>退货明细（{lines.length}）</div>
      <LineTable<ReturnLine>
        rows={lines}
        emptyText="暂无明细"
        columns={[
          { header: '物料', render: (l) => `${l.item_code ?? ''} ${l.item_name_zh ?? ''}`.trim() || l.item_id },
          { header: '数量', align: 'right', render: (l) => <span className="num">{num(l.quantity, 3)}</span> },
          { header: '单位', render: (l) => <span className="muted">{l.uom}</span> },
          { header: '序列号', render: (l) => <span className="muted">{formatSerials(l.serial_numbers)}</span> },
          { header: '批次', render: (l) => <span className="muted">{l.batch_number || '—'}</span> },
          { header: '检验', render: (l) => <Pill tone={inspectionResult(l.inspection_result).tone} label={inspectionResult(l.inspection_result).label} /> },
          { header: '备注', render: (l) => <span className="muted">{l.notes || '—'}</span> },
        ]}
      />

      {canWrite && !finished ? (
        <div className="detail-actions" style={{ marginTop: 10 }}>
          <button className="btn primary" onClick={() => setResolving((v) => !v)}>
            {resolving ? '收起处理' : '处理（回库 / 退还供应商）'}
          </button>
        </div>
      ) : null}

      {resolving && !finished ? (
        <ResolvePanel
          ret={ret}
          onCancel={() => setResolving(false)}
          onDone={() => { setResolving(false); reload(); onChanged(); }}
        />
      ) : null}
    </div>
  );
}

function formatSerials(raw?: string | null): string {
  if (!raw) return '—';
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return arr.length ? arr.join('、') : '—';
    return String(raw);
  } catch {
    return String(raw);
  }
}

// —— 新建退货 / 退料单：类型切换客户/供应商，客户退货可关联原合同/原交货，明细可录序列号/批次 ——
interface LineRow { itemId: string; label: string; quantity: string; uom: string; serials: string; batch: string; notes: string; }

function NewReturnForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [type, setType] = useState<ReturnType>('customer_return');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerLabel, setCustomerLabel] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierLabel, setSupplierLabel] = useState('');
  const [contractId, setContractId] = useState<string | null>(null);
  const [contractLabel, setContractLabel] = useState('');
  const [deliveryId, setDeliveryId] = useState<string | null>(null);
  const [deliveryLabel, setDeliveryLabel] = useState('');
  const [f, setF] = useState<Record<string, string>>({ return_date: today(), reason_code: '', reason_detail: '' });
  const [lines, setLines] = useState<LineRow[]>([{ itemId: '', label: '', quantity: '1', uom: 'Unidad', serials: '', batch: '', notes: '' }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isCustomer = type === 'customer_return';
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));
  const setLine = (i: number, p: Partial<LineRow>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...p } : l)));

  const submit = async () => {
    setErr(null);
    if (isCustomer && !customerId) { setErr('客户退货必须选择客户'); return; }
    if (!isCustomer && !supplierId) { setErr('供应商退料必须选择供应商'); return; }
    if (!f.return_date?.trim()) { setErr('退货日期为必填'); return; }
    if (!f.reason_code?.trim()) { setErr('退货原因为必填'); return; }
    const validLines = lines.filter((l) => l.itemId && Number(l.quantity) > 0);
    if (!validLines.length) { setErr('至少需要一条有效明细（选择物料且数量>0）'); return; }
    setBusy(true);
    try {
      const serialArr = (raw: string) => { const a = parseSerials(raw); return a.length ? a : undefined; };
      await api.post('/api/returns', {
        return_type: type,
        customer_id: isCustomer ? customerId : null,
        supplier_id: isCustomer ? null : supplierId,
        contract_id: isCustomer ? contractId || null : null,
        delivery_id: isCustomer ? deliveryId || null : null,
        return_date: f.return_date,
        reason_code: f.reason_code,
        reason_detail: f.reason_detail || null,
        lines: validLines.map((l) => ({
          item_id: l.itemId, quantity: Number(l.quantity), uom: l.uom || 'Unidad',
          serial_numbers: serialArr(l.serials), batch_number: l.batch || undefined, notes: l.notes || null,
        })),
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div>
          <h2>新建退货 / 退料单</h2>
          <div className="desc">写入核心服务 /api/returns · 客户退货（RMA）关联原合同/交货；供应商退料（RTS）关联供应商。此处先建草稿，处理后按方向过账库存流水（客户退货入库 / 退料出库）</div>
        </div>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="单据类型" required>
            <SelectInput value={type} onChange={(e) => setType(e.target.value as ReturnType)}>
              <option value="customer_return">客户退货（RMA）</option>
              <option value="supplier_material_return">供应商退料（RTS）</option>
            </SelectInput>
          </Field>
          {isCustomer ? (
            <>
              <Field label="客户" required>
                <SearchSelect<Customer> endpoint="/api/customers" value={customerId} label={customerLabel}
                  onChange={(row) => { setCustomerId(row?.id ?? null); setCustomerLabel(row ? `${row.code} ${row.legal_name}` : ''); setContractId(null); setContractLabel(''); setDeliveryId(null); setDeliveryLabel(''); }}
                  optionTitle={(c) => `${c.code} · ${c.legal_name}`} optionMeta={(c) => c.tax_id} placeholder="检索客户…" />
              </Field>
              <Field label="原销售合同（选填）">
                <SearchSelect<Contract> endpoint="/api/contracts" value={contractId} label={contractLabel}
                  onChange={(row) => { setContractId(row?.id ?? null); setContractLabel(row ? row.number : ''); }}
                  optionTitle={(c) => `${c.number}`} optionMeta={(c) => c.customer_name} placeholder="按合同号检索…"
                  params={customerId ? { customer_id: customerId } : undefined} />
              </Field>
              <Field label="原交货单（选填）">
                <SearchSelect<Delivery> endpoint="/api/deliveries" value={deliveryId} label={deliveryLabel}
                  onChange={(row) => { setDeliveryId(row?.id ?? null); setDeliveryLabel(row ? row.number : ''); }}
                  optionTitle={(d) => `${d.number}`} optionMeta={(d) => d.contract_number} placeholder="按交货单号检索…"
                  params={customerId ? { customer_id: customerId } : undefined} />
              </Field>
            </>
          ) : (
            <Field label="供应商" required>
              <SearchSelect<Supplier> endpoint="/api/suppliers" value={supplierId} label={supplierLabel}
                onChange={(row) => { setSupplierId(row?.id ?? null); setSupplierLabel(row ? `${row.code} ${row.legal_name}` : ''); }}
                optionTitle={(s) => `${s.code} · ${s.legal_name}`} optionMeta={(s) => s.tax_id} placeholder="检索供应商…" />
            </Field>
          )}
          <Field label="退货日期" required><DateInput value={f.return_date} onChange={(e) => set('return_date', e.target.value)} /></Field>
          <Field label="退货原因" required span>
            <TextInput value={f.reason_code} onChange={(e) => set('reason_code', e.target.value)} placeholder="如：质量问题 / 规格不符…" />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {REASON_PRESETS.map((r) => (
                <button key={r} type="button" className="btn link" style={{ padding: '2px 6px', fontSize: 12 }} onClick={() => set('reason_code', r)}>{r}</button>
              ))}
            </div>
          </Field>
          <Field label="原因说明" span><TextArea value={f.reason_detail} onChange={(e) => set('reason_detail', e.target.value)} /></Field>
        </FormGrid>

        <div className="detail-head" style={{ margin: '14px 0 6px' }}><h3 style={{ fontSize: 14 }}>退货明细</h3><div className="spacer" />
          <button className="btn link" onClick={() => setLines((ls) => [...ls, { itemId: '', label: '', quantity: '1', uom: 'Unidad', serials: '', batch: '', notes: '' }])}>+ 添加明细</button>
        </div>
        {lines.map((l, i) => (
          <div key={i} className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.8fr 1.4fr 1fr 1.2fr auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`物料 ${i + 1}`}>
              <SearchSelect<Item> endpoint="/api/items" value={l.itemId || null} label={l.label}
                onChange={(row) => setLine(i, { itemId: row?.id ?? '', label: row ? `${row.code} ${row.name_zh}` : '', uom: row && !l.uom ? row.stock_uom : l.uom })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索物料…" />
            </Field>
            <Field label="数量"><NumberInput value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></Field>
            <Field label="单位"><TextInput value={l.uom} onChange={(e) => setLine(i, { uom: e.target.value })} /></Field>
            <Field label="序列号(逗号/空格分隔)"><TextInput value={l.serials} onChange={(e) => setLine(i, { serials: e.target.value })} placeholder="SN001 SN002" /></Field>
            <Field label="批次"><TextInput value={l.batch} onChange={(e) => setLine(i, { batch: e.target.value })} /></Field>
            <Field label="备注"><TextInput value={l.notes} onChange={(e) => setLine(i, { notes: e.target.value })} /></Field>
            <button className="btn" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} title="删除该明细">×</button>
          </div>
        ))}

        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建退货/退料单'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

export function ReturnsPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'returns', 'create');
  const bump = () => setReloadSignal((n) => n + 1);

  const columns: Column<Return>[] = [
    { header: '单号', render: (r) => <span className="num strong">{r.number}</span> },
    { header: '类型', render: (r) => <Pill tone={returnType(r.return_type).tone} label={returnType(r.return_type).label} /> },
    { header: '客户 / 供应商', render: (r) => r.customer_name || r.supplier_name || '—' },
    { header: '退货日期', render: (r) => <span className="muted">{dateLabel(r.return_date)}</span> },
    { header: '原因', render: (r) => <span className="muted">{r.reason_code || '—'}</span> },
    { header: '原单', render: (r) => <span className="muted">{r.contract_number || r.delivery_number || '—'}</span> },
    { header: '处理', render: (r) => (r.disposition ? <Pill tone={returnDisposition(r.disposition).tone} label={returnDisposition(r.disposition).label} /> : <span className="muted">待处理</span>) },
    { header: '状态', render: (r) => <Pill tone={returnStatus(r.status).tone} label={returnStatus(r.status).label} /> },
  ];

  return (
    <>
      {creating ? (
        <NewReturnForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); bump(); }} />
      ) : null}
      <ListPage<Return>
        title="退货 / 退料"
        desc="客户退货 RMA / 供应商退料 RTS · /api/returns。展开查看明细与原单关联；「处理」按方向过账库存流水（客户退货入库 / 退料出库），处理后不可重复处理"
        endpoint="/api/returns"
        columns={columns}
        rowId={(r) => r.id}
        showYear={false}
        searchPlaceholder="搜索单号 / 客户 / 供应商 / 原因…"
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="returns" row={row} fields={RECORD_FIELDS['returns']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建退货 / 退料单'}</button>
        ) : undefined}
        renderDetail={(row) => <ReturnDetail row={row} onChanged={bump} />}
      />
    </>
  );
}
